/**
 * @fileoverview Plugin for handling pasted code from VS Code in the Aha! Editor.
 * If code is being pasted directly from VS Code, attempts to create a code block
 * with the appropriate language.
 */

import { type CodeElement, type EditorPlugin } from '../../types/aha-editor';
import { Range, Editor, Element, Text, Transforms } from 'slate';
import { LANGUAGES } from '../../util/prismLanguages';
import { isCodeBlockElement } from '../../types/guards';
import { getParentList } from '../ListPlugin/utils';
import CodeBlockComponentPlugin from '../CodeBlockPlugin/CodeBlockComponentPlugin';

// Conversions from VS Code language IDs to Prism language IDs.
// See https://code.visualstudio.com/docs/languages/identifiers
// and https://prismjs.com/#supported-languages
const LANGUAGE_MAPPINGS = {
  csharp: 'clike',
  cpp: 'clike',
  c: 'clike',
  less: 'css',
  scss: 'css',
  sass: 'css',
  'objective-c': 'objectivec',
  javascript: 'tsx',
  typescript: 'tsx',
  javascriptreact: 'tsx',
  typescriptreact: 'tsx',
  ruby: 'rb',
  shellscript: 'bash',
  xml: 'markup',
} as const;

type VSCodeLanguage = keyof typeof LANGUAGE_MAPPINGS;

interface VsCodeData {
  mode?: VSCodeLanguage; // Language string, e.g. "javascript", "rb" etc.
}

// Returns a Prism language code if we support it, otherwise undefined.
function getLanguageForPrism(language: VSCodeLanguage | undefined) {
  // Check if we need to convert from VS Code to Prism
  let prismLanguage: string | undefined = language;
  if (language && language in LANGUAGE_MAPPINGS) {
    prismLanguage = LANGUAGE_MAPPINGS[language];
  }

  // Check the language is one we support
  if (prismLanguage && prismLanguage in LANGUAGES) {
    return prismLanguage;
  }
}

const PasteFromVSCodePlugin: EditorPlugin = {
  name: 'Paste from VS Code plugin',
  dependencies: [CodeBlockComponentPlugin],
  /**
   * Handles pasting from VS Code.
   * @param editor - The Slate editor instance
   * @param data - DataTransfer object containing HTML data
   * @param controller - Optional editor controller for additional context
   * @returns true if handled, false if not applicable
   */
  insertData: (editor: Editor, data: DataTransfer) => {
    const { selection } = editor;
    if (!selection) return false;

    // Not applicable if if we don't have the right data types
    if (
      !data.types.includes(`vscode-editor-data`) ||
      !data.types.includes('text/plain')
    ) {
      return false;
    }

    const vsCodeString = data.getData('vscode-editor-data');

    // If not pasting from VS Code, return false so next plugin can handle it.
    if (!vsCodeString) return false;

    let vsCodeData: VsCodeData = {};
    try {
      vsCodeData = JSON.parse(vsCodeString) as VsCodeData;
    } catch (e) {
      return false;
    }

    // A supported language, or undefined. If undefined we'll insert
    // a plain code block.
    const language = getLanguageForPrism(vsCodeData.mode);

    const textData = data.getData('text/plain');

    // Not a multiline paste, let another plugin handle it
    if (!textData.includes('\n')) {
      return false;
    }

    // Skip if we're in a list - the handling is too complicated
    if (getParentList(editor, editor.selection?.anchor)) {
      return false;
    }

    const selectionStart = Range.start(selection);
    const startBlock = Editor.above(editor, {
      at: selectionStart,
      mode: 'lowest',
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        Editor.isBlock(editor, n),
    });
    if (!startBlock) return false;

    // If we're already in a code block, let the CodeBlockPastePlugin handle it
    if (isCodeBlockElement(startBlock[0])) return false;

    if (
      startBlock[0].children.length === 1 &&
      Text.isText(startBlock[0].children[0]) &&
      startBlock[0].children[0].text === ''
    ) {
      // Current block is empty, convert it to a code block
      Transforms.setNodes(editor, {
        type: 'code',
        code_language: language,
      });
      Transforms.insertText(editor, textData);
    } else {
      // Otherwise insert a new code block after the current block
      const codeElement = {
        type: 'code',
        code_language: language,
        children: [{ text: textData }],
      } as CodeElement;
      Transforms.insertNodes(editor, codeElement);
    }
    return true;
  },
};

export default PasteFromVSCodePlugin;
